import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  DollarSign, TrendingDown, ShieldCheck, RefreshCw, Building2, Activity, Clock,
  Sparkles, ArrowUpRight, Brain, Lightbulb, FileText, FileSpreadsheet, FileType, Loader2, AlertTriangle, ChevronDown, ChevronRight, X, AlertOctagon, CheckCircle2, Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Legend, LineChart, Line, LabelList,
} from "recharts";
import { AppealRecommendations } from "@/components/AppealRecommendations";
import { KpiDrillDownModal } from "@/components/dashboard/KpiDrillDownModal";
import { TopPayorRiskPanel } from "@/components/dashboard/TopPayorRiskPanel";
import { DenialIntelligenceTrendsPanel } from "@/components/dashboard/DenialIntelligencePanel";
import { HumanInTheLoopCard } from "@/components/HumanInTheLoopCard";
import { PlanUsageWidget } from "@/components/PlanUsageWidget";

const NAVY = "#0F4C81";
const BLUE = "#1E88E5";
const EMERALD = "#26A69A";
const AMBER = "#546E7A";
const SKY = "#7E57C2";
const PURPLE = "#90A4AE";

// Enterprise palette for denial categories — muted, executive-friendly.
const CATEGORY_COLORS = {
  "Authorization Missing": "#1E88E5",
  "COB Issues": "#7E57C2",
  "COB Issue": "#7E57C2",
  "Coding Errors": "#26A69A",
  "Coding Error": "#26A69A",
  "Medical Necessity": "#546E7A",
  "Timely Filing": "#90A4AE",
  "Eligibility": "#5C6BC0",
  "Missing Documentation": "#00897B",
  "Duplicate Claim": "#78909C",
  "Service Not Covered": "#455A64",
  "Provider Credentialing": "#8E24AA",
  "DRG Validation": "#3949AB",
  "Bundling": "#00ACC1",
  "Modifier Issue": "#607D8B",
};
const FALLBACK_PALETTE = ["#1E88E5", "#26A69A", "#7E57C2", "#546E7A", "#5C6BC0", "#00897B", "#90A4AE", "#3949AB"];
const colorForCategory = (name, idx = 0) => CATEGORY_COLORS[name] || FALLBACK_PALETTE[idx % FALLBACK_PALETTE.length];

const fmtMoney = (n, compact = true) =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
    notation: compact ? "compact" : "standard", maximumFractionDigits: compact ? 1 : 2,
  }).format(n || 0);

const fmtPct = (n) => `${(n ?? 0).toFixed(1)}%`;
const fmtNum = (n) => new Intl.NumberFormat("en-US").format(n || 0);

const BenchmarkBadge = ({ grade, testId }) => {
  if (!grade || grade.status === "n/a" || grade.median === null) return null;
  const styles = {
    ahead:    { bg: "rgba(45,212,191,0.16)",  border: "rgba(45,212,191,0.45)", color: "#5EEAD4", arrow: "↓", label: grade.label?.replace("+", "") },
    behind:   { bg: "rgba(251,146,60,0.14)",  border: "rgba(251,146,60,0.45)", color: "#FDBA74", arrow: "↑", label: grade.label },
    on_track: { bg: "rgba(255,255,255,0.08)", border: "rgba(255,255,255,0.18)", color: "#E2E8F0", arrow: "→", label: "On industry median" },
  }[grade.status];
  return (
    <span data-testid={testId} className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md mt-2"
      style={{ backgroundColor: styles.bg, border: `1px solid ${styles.border}`, color: styles.color }}>
      <span>{styles.arrow}</span>{styles.label}
    </span>
  );
};

const PeriodArrow = ({ delta, testId }) => {
  if (!delta || delta.direction === "flat") return null;
  const up = delta.direction === "improved";
  const color = up ? "#5EEAD4" : "#FCA5A5";
  const arrow = delta.delta_pct > 0 ? "↑" : "↓";
  return (
    <span data-testid={testId} className="ml-1.5 text-[10px] font-semibold" style={{ color }}>
      {arrow} {Math.abs(delta.delta_pct).toFixed(1)}% {up ? "better" : "worse"} vs prior
    </span>
  );
};

const KpiCard = ({ icon: Icon, label, value, sub, tone = "navy", testId, grade, period, hero = false, helperText = "", onClick = null }) => {
  // Unified premium navy KPI card — same gradient for ALL boxes.
  // `hero` adds a touch more emphasis to the primary "Total Denied Dollars" tile.
  // `onClick` makes the card act as a drill-down trigger.
  const clickable = typeof onClick === "function";
  return (
    <div
      data-testid={testId}
      onClick={clickable ? onClick : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? `Open ${label} details` : undefined}
      className={`group relative overflow-hidden rounded-xl transition-all duration-300 ${
        clickable ? "cursor-pointer hover:-translate-y-1 hover:ring-1 hover:ring-teal-300/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-300/60" : "hover:-translate-y-0.5"
      } ${hero ? "p-6 ring-1 ring-teal-300/15" : "p-5"}`}
      style={{
        background: hero
          ? "linear-gradient(160deg, #0C1828 0%, #142841 55%, #173050 100%)"
          : "linear-gradient(160deg, #0B1524 0%, #122236 55%, #14283F 100%)",
        border: `1px solid ${hero ? "rgba(94,234,212,0.22)" : "rgba(94,234,212,0.14)"}`,
        boxShadow: hero
          ? "0 18px 38px -20px rgba(0,0,0,0.85), 0 0 0 1px rgba(94,234,212,0.10), 0 1px 0 rgba(255,255,255,0.04) inset"
          : "0 12px 28px -18px rgba(0,0,0,0.80), 0 0 0 1px rgba(94,234,212,0.05), 0 1px 0 rgba(255,255,255,0.04) inset",
      }}
    >
      {/* Subtle teal corner glow — slightly stronger on the hero */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute -top-12 -right-12 rounded-full ${hero ? "h-40 w-40" : "h-32 w-32"}`}
        style={{
          background: `radial-gradient(closest-side, rgba(20,184,166,${hero ? 0.28 : 0.18}), transparent 70%)`,
        }}
      />
      {/* Hairline accent at the top */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-px"
        style={{ background: "linear-gradient(90deg, transparent 0%, rgba(20,184,166,0.55) 50%, transparent 100%)" }}
      />

      <div className="relative flex items-start justify-between">
        <div
          className={`rounded-lg flex items-center justify-center ${hero ? "h-10 w-10" : "h-9 w-9"}`}
          style={{
            background: "rgba(20,184,166,0.14)",
            border: "1px solid rgba(20,184,166,0.32)",
            boxShadow: "0 0 0 4px rgba(20,184,166,0.06)",
          }}
        >
          <Icon style={{ width: hero ? 20 : 18, height: hero ? 20 : 18, color: "#5EEAD4" }} />
        </div>
        <div className="flex items-center gap-1.5">
          {period && period.direction !== "flat" && (
            <TrendChip delta={period} testId={`${testId}-period`} />
          )}
          {clickable && (
            <ChevronRight className="h-4 w-4 text-teal-300/50 transition-transform group-hover:translate-x-0.5 group-hover:text-teal-300/90" aria-hidden="true" />
          )}
        </div>
      </div>

      <div
        className={`relative font-semibold uppercase ${hero ? "mt-5 text-[12px] tracking-[0.16em]" : "mt-4 text-[11px] tracking-[0.14em]"}`}
        style={{ color: hero ? "#E2E8F0" : "#E2E8F0" }}
      >
        {label}
      </div>
      <div
        className={`relative font-display font-semibold tracking-tight ${
          hero ? "mt-2.5 text-4xl xl:text-[2.4rem]" : "mt-1.5 text-2xl xl:text-3xl"
        }`}
        style={{ color: "#FFFFFF", letterSpacing: "-0.02em" }}
      >
        {value}
      </div>
      {sub && (
        <div className={`relative ${hero ? "mt-2 text-[13px]" : "mt-1.5 text-xs"}`} style={{ color: "#E2E8F0" }}>
          {sub}
        </div>
      )}
      {helperText && (
        <div className="relative mt-1.5 text-[11px] leading-snug" style={{ color: "#94A3B8" }}>
          {helperText}
        </div>
      )}
      <BenchmarkBadge grade={grade} testId={`${testId}-benchmark`} />
    </div>
  );
};

/**
 * Compact trend pill used only on the premium dark KPI card.
 * Muted red/amber for negative trends, muted teal for positive.
 */
const TrendChip = ({ delta, testId }) => {
  const dir = delta.direction;
  const rawPct = Math.abs(delta.delta_pct || 0);
  // Cap absurd swings from sparse / partial-period data so we don't surface
  // unrealistic 400%+ chips on small samples.
  if (rawPct > 100) return null;
  const pct = rawPct.toFixed(1);
  const isImproved = dir === "improved";
  const palette = isImproved
    ? { bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.35)", color: "#6EE7B7", arrow: "↓" }
    : { bg: "rgba(239,68,68,0.12)",  border: "rgba(239,68,68,0.35)",  color: "#FCA5A5", arrow: "↑" };
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-md"
      style={{ background: palette.bg, border: `1px solid ${palette.border}`, color: palette.color }}
      title={`${pct}% ${isImproved ? "better" : "worse"} vs prior period`}
    >
      <span aria-hidden="true">{palette.arrow}</span>
      {pct}%
    </span>
  );
};

const SectionHeader = ({ overline, title, action }) => (
  <div className="flex items-end justify-between mb-4">
    <div>
      <div className="label-overline">{overline}</div>
      <h2 className="font-display text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight mt-1">{title}</h2>
    </div>
    {action}
  </div>
);

const ChartTooltip = ({ active, payload, label, valueFormat = fmtMoney }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg bg-navy text-white text-xs px-3 py-2 shadow-lg border border-slate-800">
      <div className="font-semibold mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-blue-200">{p.name}:</span>
          <span className="font-medium">{valueFormat(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// ---------- Premium accent panels (light surface + teal accent) ----------

const DeltaPill = ({ delta }) => {
  if (!delta || delta.direction === "flat") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md uppercase"
        style={{ background: "rgba(20,184,166,0.10)", border: "1px solid rgba(20,184,166,0.35)", color: "#2DD4BF" }}>
        12 months
      </span>
    );
  }
  // Cap noisy pct from partial-period datasets (e.g. only 5 mo of demo data)
  const rawPct = Math.abs(delta.delta_pct || 0);
  if (rawPct > 80) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md uppercase"
        style={{ background: "rgba(20,184,166,0.10)", border: "1px solid rgba(20,184,166,0.35)", color: "#2DD4BF" }}>
        Rolling 30d
      </span>
    );
  }
  const improved = delta.direction === "improved";
  const palette = improved
    ? { bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.35)", color: "#86EFAC", arrow: "↓" }
    : { bg: "rgba(239,68,68,0.10)",  border: "rgba(239,68,68,0.35)",  color: "#FCA5A5", arrow: "↑" };
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md"
      style={{ background: palette.bg, border: `1px solid ${palette.border}`, color: palette.color }}>
      <span>{palette.arrow}</span> {rawPct.toFixed(1)}% {improved ? "vs prior month" : "vs prior month"}
    </span>
  );
};

const TopDenialDriversPanel = ({ drivers, months = 0, onSelect = null }) => {
  const top5 = drivers.slice(0, 5);
  const maxPct = Math.max(...top5.map((d) => d.pct_of_total || 0), 1);
  return (
    <div
      className="surface-card p-6 relative overflow-hidden"
      data-testid="top-denial-drivers"
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: "#2DD4BF" }}>
            Top 5 Denial Drivers
          </div>
          <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight mt-1.5 text-slate-900">
            Operational Groupings, Not Raw Codes
          </h2>
        </div>
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md uppercase"
          style={{ background: "rgba(20,184,166,0.10)", border: "1px solid rgba(20,184,166,0.35)", color: "#2DD4BF" }}>
          {months} {months === 1 ? "month" : "months"}
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-5">
        ANSI/CARC denial codes translated into operational root-cause categories.
      </p>

      <div className="space-y-3">
        {top5.length === 0 && (
          <div className="text-sm py-6 text-center text-slate-500">No driver data yet.</div>
        )}
        {top5.map((d, i) => {
          const widthPct = Math.max(8, (d.pct_of_total / maxPct) * 100);
          const clickable = typeof onSelect === "function";
          return (
            <button
              type="button"
              key={d.driver}
              data-testid={`driver-row-${i}`}
              onClick={clickable ? () => onSelect(d) : undefined}
              disabled={!clickable}
              className={`w-full text-left grid items-center gap-x-4 px-3 py-2.5 -mx-3 rounded-lg transition-all ${
                clickable ? "cursor-pointer hover:bg-teal-50/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400" : ""
              }`}
              style={{
                gridTemplateColumns: clickable
                  ? "24px minmax(0, 1.5fr) minmax(0, 2fr) 48px 56px 96px 16px"
                  : "24px minmax(0, 1.5fr) minmax(0, 2fr) 48px 56px 96px",
              }}
            >
              <div className="text-[10px] font-semibold text-slate-400 tabular-nums">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="text-sm font-semibold truncate text-slate-900" title={d.driver}>
                {d.driver}
              </div>
              <div className="h-2 rounded-full overflow-hidden bg-slate-100">
                <div className="h-full rounded-full transition-all"
                  style={{
                    width: `${widthPct}%`,
                    background: "linear-gradient(90deg, #14B8A6 0%, #5EEAD4 100%)",
                  }} />
              </div>
              <span className="text-xs font-bold text-right tabular-nums" style={{ color: "#5EEAD4" }}>
                {fmtPct(d.pct_of_total)}
              </span>
              <div className="text-right text-sm font-mono text-slate-700 tabular-nums">
                {fmtNum(d.denial_count)}
              </div>
              <div className="text-right text-sm font-mono font-semibold text-slate-900 tabular-nums">
                {fmtMoney(d.denial_dollars)}
              </div>
              {clickable && (
                <ChevronRight className="h-3.5 w-3.5 text-slate-300" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-6 pt-4 grid grid-cols-4 gap-4 text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500 border-t border-slate-100">
        <div>Driver</div>
        <div>% of Denials</div>
        <div className="text-right">Count</div>
        <div className="text-right">$ at Risk</div>
      </div>
    </div>
  );
};

const DenialsByPayorPanel = ({ payors, totalClaims = 1, onSelect = null }) => {
  const [selectedPayor, setSelectedPayor] = useState("__all__");
  const filtered = selectedPayor === "__all__"
    ? payors
    : payors.filter((p) => p.payor === selectedPayor);
  const top5 = filtered.slice(0, 5);
  const maxVolume = Math.max(...top5.map((p) => p.volume || 0), 1);
  return (
    <div
      className="surface-card p-6 relative overflow-hidden"
      data-testid="denials-by-payor-panel"
    >
      <div className="mb-2 flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: "#2DD4BF" }}>
            Payor Analysis
          </div>
          <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight mt-1.5 text-slate-900">
            Payor Denial Concentration
          </h2>
        </div>
        {payors.length > 1 && (
          <select
            value={selectedPayor}
            onChange={(e) => setSelectedPayor(e.target.value)}
            data-testid="payor-concentration-filter"
            className="shrink-0 h-8 rounded-md border border-slate-300 bg-white px-2 text-[11px] font-semibold text-slate-700 max-w-[180px] truncate focus:outline-none focus:ring-2 focus:ring-teal-400/40"
            title="Filter to a specific payor"
          >
            <option value="__all__">All Payors ({payors.length})</option>
            {payors.map((p) => (
              <option key={p.payor} value={p.payor}>{p.payor}</option>
            ))}
          </select>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-5">
        Highest concentration of denied dollars by payor.
      </p>

      <div className="space-y-3">
        {top5.length === 0 && (
          <div className="text-sm py-6 text-center text-slate-500">No payor data yet.</div>
        )}
        {top5.map((p) => {
          const widthPct = Math.max(8, (p.volume / maxVolume) * 100);
          const denialRate = totalClaims ? (p.volume / totalClaims) * 100 : 0;
          const high = denialRate > 6;
          const pill = high
            ? { bg: "rgba(248,113,113,0.20)", border: "rgba(248,113,113,0.65)", color: "#FCA5A5" }
            : { bg: "rgba(45,212,191,0.22)",  border: "rgba(45,212,191,0.65)",  color: "#5EEAD4" };
          const clickable = typeof onSelect === "function";
          return (
            <button
              type="button"
              key={p.payor}
              data-testid={`payor-row-${p.payor.toLowerCase().replace(/\W+/g, "-")}`}
              onClick={clickable ? () => onSelect(p) : undefined}
              disabled={!clickable}
              className={`w-full text-left flex items-center gap-4 px-3 py-2.5 -mx-3 rounded-lg transition-all ${
                clickable ? "cursor-pointer hover:bg-teal-50/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400" : ""
              }`}
            >
              <div className="w-32 shrink-0 text-sm font-semibold truncate text-slate-900" title={p.payor}>
                {p.payor}
              </div>
              <div className="flex-1 h-2 rounded-full overflow-hidden bg-slate-100">
                <div className="h-full rounded-full"
                  style={{
                    width: `${widthPct}%`,
                    background: "linear-gradient(90deg, #14B8A6 0%, #5EEAD4 100%)",
                  }} />
              </div>
              <div className="w-12 text-right text-sm font-mono text-slate-700 tabular-nums">
                {fmtNum(p.volume)}
              </div>
              <span className="inline-flex items-center justify-center text-[11px] font-bold w-14 py-0.5 rounded-md tabular-nums"
                style={{ background: pill.bg, border: `1px solid ${pill.border}`, color: pill.color }}>
                {denialRate.toFixed(1)}%
              </span>
              {clickable && (
                <ChevronRight className="h-3.5 w-3.5 text-slate-300 shrink-0" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Actionability breakdown card — splits denials into "worth working" vs
// "already lost" so the customer sees where their team should focus.
// Pure CARC-based classification, specialty-agnostic.
const ActionabilityCard = ({ actionability }) => {
  if (!actionability) return null;
  const a = actionability.actionable || {};
  const na = actionability.non_actionable || {};
  const reasons = actionability.non_actionable_reasons || [];
  const totalDollars = (a.dollars || 0) + (na.dollars || 0);
  const totalClaims = (a.count || 0) + (na.count || 0);
  if (totalClaims === 0) return null;

  const actPct = a.pct_of_dollars || 0;
  const naPct = na.pct_of_dollars || 0;

  return (
    <div
      className="surface-card p-6 relative overflow-hidden"
      data-testid="actionability-card"
    >
      <div aria-hidden="true" className="pointer-events-none absolute -top-12 -right-12 h-44 w-44 rounded-full"
        style={{ background: "radial-gradient(closest-side, rgba(20,184,166,0.18), transparent 70%)" }} />

      <div className="relative">
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: "#5EEAD4" }}>
          <Lightbulb className="h-3 w-3" />
          Denial Actionability
        </div>
        <h2 className="font-display text-xl font-semibold tracking-tight mt-1.5 text-white">
          Where Your Team Should Focus
        </h2>
        <p className="text-xs text-slate-300 mt-1">
          Deterministic CARC-based classification. Non-actionable denials cannot be recovered no matter how many hours your team spends on them.
        </p>

        {/* Split bar */}
        <div className="mt-5 h-4 w-full rounded-full overflow-hidden flex bg-slate-800/70 border border-white/10" data-testid="actionability-split-bar">
          <div className="h-full transition-all duration-700"
               style={{ width: `${actPct}%`, background: "linear-gradient(90deg, #14B8A6, #2DD4BF)" }} />
          <div className="h-full transition-all duration-700"
               style={{ width: `${naPct}%`, background: "linear-gradient(90deg, #F97316, #FB923C)" }} />
        </div>

        {/* Two-column stats */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-teal-400/40 bg-teal-500/10 p-3.5" data-testid="actionable-stat">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-teal-300">
              <span className="h-2 w-2 rounded-full bg-teal-400 inline-block" />
              Actionable
            </div>
            <div className="mt-1.5 flex items-baseline gap-2">
              <span className="text-2xl font-bold font-display text-white tabular-nums">${(a.dollars || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              <span className="text-xs font-semibold text-teal-300">{actPct}%</span>
            </div>
            <div className="mt-0.5 text-[11px] text-slate-300">
              {a.count} {a.count === 1 ? "claim" : "claims"} · Appealable / correctable / retro-eligible
            </div>
          </div>

          <div className="rounded-xl border border-orange-400/40 bg-orange-500/10 p-3.5" data-testid="non-actionable-stat">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-orange-300">
              <span className="h-2 w-2 rounded-full bg-orange-400 inline-block" />
              Non-Actionable
            </div>
            <div className="mt-1.5 flex items-baseline gap-2">
              <span className="text-2xl font-bold font-display text-white tabular-nums">${(na.dollars || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              <span className="text-xs font-semibold text-orange-300">{naPct}%</span>
            </div>
            <div className="mt-0.5 text-[11px] text-slate-300">
              {na.count} {na.count === 1 ? "claim" : "claims"} · Write-off — not worth staff time
            </div>
          </div>
        </div>

        {/* Non-actionable reason breakdown (if any) */}
        {reasons.length > 0 && (
          <div className="mt-4 rounded-lg border border-white/10 bg-white/5 p-3.5" data-testid="non-actionable-reasons">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-300 mb-2">
              Why they&apos;re not worth working
            </div>
            <ul className="space-y-1.5">
              {reasons.slice(0, 5).map((r) => (
                <li key={r.reason} className="flex items-start justify-between gap-3 text-[12px]">
                  <span className="text-slate-200 leading-tight">{r.reason}</span>
                  <span className="shrink-0 tabular-nums font-semibold text-white">
                    {r.count} · ${r.dollars.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4 rounded-md bg-teal-500/15 border border-teal-400/40 px-3 py-2 text-[11.5px] text-teal-100">
          <span className="font-semibold text-white">Focus your team&apos;s time on the ${(a.dollars || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}.</span>
          {" "}The remaining ${(na.dollars || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} won&apos;t come back no matter how many appeals you write.
        </div>
      </div>
    </div>
  );
};


const TopOpportunityCard = ({ data }) => {
  // Pick the highest recovery_opportunity driver
  const top = (data?.denial_drivers || []).slice().sort((a, b) => (b.recovery_opportunity || 0) - (a.recovery_opportunity || 0))[0];
  if (!top) {
    return (
      <div className="surface-card p-6 relative overflow-hidden" data-testid="top-opportunity-card">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: "#2DD4BF" }}>
          Prioritized Recovery
        </div>
        <h2 className="font-display text-xl font-semibold tracking-tight mt-1.5 text-slate-900">
          Highest Recovery Opportunity
        </h2>
        <div className="text-sm mt-6 text-slate-500">Load a dataset to see your top recovery opportunity.</div>
      </div>
    );
  }
  const topCarc = top.top_carc?.[0]?.code;
  const codeLabel = topCarc ? `CO-${topCarc}` : "Multi-code";
  const winLikelihood = top.avg_recovery_likelihood || 65;
  // Cleaner title — "CO-197 Authorization Denials" rather than "Bundled CO-197 appeals — Authorization"
  const headline = topCarc
    ? `${codeLabel} ${top.driver} Denials`
    : `${top.driver} Recovery Opportunity`;

  return (
    <div
      className="surface-card p-6 relative overflow-hidden"
      data-testid="top-opportunity-card"
    >
      {/* corner glow */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-12 -right-12 h-44 w-44 rounded-full"
        style={{ background: "radial-gradient(closest-side, rgba(20,184,166,0.22), transparent 70%)" }} />

      <div className="relative">
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: "#2DD4BF" }}>
          <Sparkles className="h-3 w-3" />
          Prioritized Recovery
        </div>
        <h2 className="font-display text-xl font-semibold tracking-tight mt-1.5 text-slate-900">
          Highest Recovery Opportunity
        </h2>

        <div className="mt-5 flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0"
               style={{ background: "rgba(20,184,166,0.18)", border: "1px solid rgba(20,184,166,0.45)" }}>
            <Brain className="h-5 w-5" style={{ color: "#5EEAD4" }} />
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg font-semibold leading-tight" style={{ color: "#FFFFFF" }}>
              {headline}
            </div>
            <div className="text-xs mt-1" style={{ color: "#CBD5E1" }}>
              {fmtNum(top.denial_count)} claims · {top.top_carc?.slice(0, 3).map((c) => `CO-${c.code}`).join(" · ") || top.driver}
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="rounded-xl p-4"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
            }}>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: "#CBD5E1" }}>
              Recovery Estimate
            </div>
            <div className="font-display text-2xl font-semibold mt-1.5 tabular-nums" style={{ color: "#FFFFFF" }}>
              {fmtMoney(top.recovery_opportunity)}
            </div>
          </div>
          <div className="rounded-xl p-4"
            style={{
              background: "rgba(45,212,191,0.10)",
              border: "1px solid rgba(45,212,191,0.30)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
            }}>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: "#5EEAD4" }}>
              Recovery Probability
            </div>
            <div className="font-display text-2xl font-semibold mt-1.5 tabular-nums" style={{ color: "#5EEAD4" }}>
              {winLikelihood}%
            </div>
          </div>
        </div>

        {/* Dedicated Recommended Action block — designed to read on both light + dark themes. */}
        <div className="mt-5 rounded-xl p-4"
          style={{
            border: "1px solid rgba(45,212,191,0.45)",
            background: "linear-gradient(160deg, rgba(20,184,166,0.10) 0%, rgba(20,184,166,0.04) 100%)",
            boxShadow: "0 0 24px -10px rgba(20,184,166,0.25), inset 0 1px 0 rgba(255,255,255,0.04)",
          }}
        >
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em]" style={{ color: "#5EEAD4" }}>
            <Lightbulb className="h-3 w-3" />
            Recommended Action
          </div>
          <p className="text-sm leading-relaxed mt-2 font-medium" style={{ color: "#F1F5F9" }}>
            {top.recommended_action}
          </p>
          <div className="mt-3 flex items-center gap-1.5 text-[11px]" style={{ color: "#94A3B8" }}>
            <Sparkles className="h-2.5 w-2.5" style={{ color: "#5EEAD4" }} />
            <span>Prioritized based on recoverability scoring · Modeled on ANSI/CARC resolution trends</span>
          </div>
        </div>
      </div>
    </div>
  );
};




// ---------- Payor Denial Intelligence (executive risk vs opportunity bar chart) ----------
/**
 * Replaces the previous "Top Payor Denial Analytics" bar chart.
 *  - Same horizontal-bar layout (executive-friendly), but with semantic financial colors:
 *      muted coral = denied / at-risk dollars
 *      teal cyan  = recoverable opportunity (slightly emphasized — the actionable lever)
 *  - Three quick KPI tiles above the chart for instant financial storytelling
 *  - Inline $ data labels on each bar (no need to hover)
 *  - Smooth fade-in animation, premium spacing & typography
 */
/**
 * RecoveryOutlookPanel — CFO-grade Recoverable Revenue Calculator
 * ------------------------------------------------------------------
 * Multiplies each denial's $ × its CARC-derived recovery_likelihood to surface
 * a single "Expected Recovery" number, plus a projected quarterly runrate and
 * the top 3 drivers of that recoverable pool. This is the executive headline.
 */
const RecoveryOutlookPanel = ({ outlook }) => {
  if (!outlook || outlook.total_denied === 0) return null;
  const {
    expected_recovery, high_confidence_recovery, total_denied, recovery_rate_pct,
    quarter_projection, appealable_dollars, appealable_count,
    months_in_dataset, top_drivers = [],
  } = outlook;
  const expectedShare = total_denied ? (expected_recovery / total_denied) * 100 : 0;
  const highConfShare = expected_recovery ? (high_confidence_recovery / expected_recovery) * 100 : 0;

  return (
    <div
      data-testid="recovery-outlook-panel"
      className="relative overflow-hidden rounded-2xl border border-teal-500/30 bg-gradient-to-br from-slate-900 via-[#0d1e2c] to-slate-900 p-6 lg:p-8 shadow-[0_0_40px_-12px_rgba(20,184,166,0.35)]"
    >
      <div className="absolute right-0 top-0 h-40 w-40 -translate-y-12 translate-x-12 rounded-full bg-teal-500/20 blur-3xl" aria-hidden />
      <div className="absolute left-0 bottom-0 h-32 w-32 translate-y-10 -translate-x-10 rounded-full bg-cyan-500/10 blur-3xl" aria-hidden />
      <div className="relative grid lg:grid-cols-[1.2fr_1fr_1.4fr] gap-6 lg:gap-10">
        {/* Hero number */}
        <div data-testid="recovery-outlook-hero">
          <span className="label-overline inline-flex items-center gap-1.5 text-teal-300">
            <RefreshCw className="h-3 w-3" /> Recoverable Revenue
          </span>
          <div className="text-[40px] lg:text-[52px] font-display font-semibold text-white leading-none mt-2 tabular-nums tracking-tight">
            {fmtMoney(expected_recovery)}
          </div>
          <div className="mt-2 text-sm text-slate-300">
            Expected recovery from <span className="font-semibold text-white tabular-nums">{fmtMoney(total_denied)}</span> in denials
          </div>
          <div className="mt-3 inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-teal-500/15 border border-teal-400/30 text-[11px] font-semibold text-teal-200">
            <span className="tabular-nums">{recovery_rate_pct}%</span>
            <span className="opacity-80">overall recovery rate</span>
          </div>
        </div>

        {/* Secondary numbers */}
        <div className="grid grid-cols-2 lg:grid-cols-1 gap-3 lg:gap-2.5">
          <RecoveryStat
            testid="recovery-outlook-high-confidence"
            label="High-confidence recovery"
            value={fmtMoney(high_confidence_recovery)}
            footnote={`AI confidence ≥60% · ${highConfShare.toFixed(0)}% of expected`}
            tone="emerald"
          />
          <RecoveryStat
            testid="recovery-outlook-quarter"
            label="Projected Quarterly Recovery"
            value={fmtMoney(quarter_projection)}
            footnote={months_in_dataset < 3
              ? `Forecast from ${months_in_dataset} mo of data`
              : "Based on current resolution pace"}
            tone="teal"
          />
          <RecoveryStat
            testid="recovery-outlook-appealable"
            label="Appealable accounts"
            value={`${fmtNum(appealable_count)}`}
            footnote={`${fmtMoney(appealable_dollars)} in revenue at stake`}
            tone="slate"
          />
        </div>

        {/* Top driver attribution */}
        <div data-testid="recovery-outlook-drivers">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-2">
            Top contributors
          </div>
          <div className="space-y-2.5">
            {top_drivers.map((d) => {
              const share = expected_recovery ? (d.expected / expected_recovery) * 100 : 0;
              return (
                <div key={d.driver} className="space-y-1" data-testid={`recovery-driver-${(d.driver || "").toLowerCase().replace(/\W+/g, "-")}`}>
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="font-semibold text-slate-100 truncate pr-2">{d.driver}</span>
                    <span className="tabular-nums text-teal-300 font-semibold">{fmtMoney(d.expected)}</span>
                  </div>
                  <div className="h-1.5 w-full bg-slate-700/60 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-teal-400 to-cyan-400 rounded-full transition-all duration-700"
                      style={{ width: `${Math.min(Math.max(share, 4), 100)}%` }}
                    />
                  </div>
                  <div className="text-[12px] text-slate-300 tabular-nums">{share.toFixed(0)}% of recoverable · of {fmtMoney(d.dollars)} denied</div>
                </div>
              );
            })}
            {top_drivers.length === 0 && (
              <div className="text-xs text-slate-400 italic">No driver attribution available.</div>
            )}
          </div>
          <div className="mt-3 text-[12px] text-slate-300 leading-relaxed">
            AI analyzed ANSI/CARC denial patterns and identified{" "}
            <span className="font-semibold text-white tabular-nums">{fmtNum(appealable_count)}</span>{" "}
            high-value recoverable accounts representing{" "}
            <span className="font-semibold text-white tabular-nums">{expectedShare.toFixed(0)}%</span>{" "}
            of total denied dollars.
          </div>
        </div>
      </div>
    </div>
  );
};

const RecoveryStat = ({ label, value, footnote, tone = "slate", testid }) => {
  const tones = {
    emerald: "border-emerald-400/40 bg-emerald-500/[0.12] shadow-[0_2px_18px_-8px_rgba(16,185,129,0.55)] ring-1 ring-emerald-300/10",
    teal:    "border-teal-400/40 bg-teal-500/[0.12] shadow-[0_2px_18px_-8px_rgba(20,184,166,0.55)] ring-1 ring-teal-300/10",
    slate:   "border-slate-500/40 bg-slate-800/50 shadow-[0_2px_18px_-8px_rgba(15,23,42,0.7)] ring-1 ring-slate-400/10",
  };
  return (
    <div data-testid={testid} className={`rounded-xl border ${tones[tone] || tones.slate} px-3.5 py-3 backdrop-blur-sm`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-200">{label}</div>
      <div className="text-xl lg:text-2xl font-display font-semibold text-white mt-1 tabular-nums leading-none">
        {value}
      </div>
      <div className="text-[12px] text-slate-300 mt-1.5">{footnote}</div>
    </div>
  );
};



/**
 * PayorIntelligenceTooltip — premium dark floating card used by the
 * Payor Denial Intelligence bar chart. Replaces the basic `ChartTooltip`
 * with a richer payload (recovery probability + AI insight per payor).
 */
const PayorIntelligenceTooltip = ({ active, payload, totalDenied = 0 }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  if (!p) return null;
  const denied = p.dollars || 0;
  const recoverable = p.recoverable_dollars || 0;
  const probability = denied > 0 ? (recoverable / denied) * 100 : 0;
  const share = totalDenied > 0 ? (denied / totalDenied) * 100 : 0;
  // Heuristic AI insight bullet — varies by which lever has the most room.
  let insight = "Authorization and Medical Necessity denials are typically the highest recovery levers.";
  if (probability > 65) insight = "High recovery probability — prioritize this payor for appeal workflows this week.";
  else if (probability > 50) insight = "Solid recoverable pool — Authorization-driven denials often yield highest results.";
  else if (probability < 35) insight = "Tighten front-end controls — preventable denials concentrate operational losses here.";

  return (
    <div
      className="rounded-xl border min-w-[280px] backdrop-blur-md"
      style={{
        background: "linear-gradient(160deg, rgba(11,21,36,0.96) 0%, rgba(18,34,54,0.96) 100%)",
        borderColor: "rgba(94,234,212,0.35)",
        boxShadow: "0 24px 48px -16px rgba(0,0,0,0.7), 0 0 0 1px rgba(94,234,212,0.10), 0 0 40px -10px rgba(20,184,166,0.35)",
      }}
    >
      <div className="px-4 pt-3.5 pb-2.5 border-b border-slate-700/40">
        <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-teal-300">Payor</div>
        <div className="font-display text-base font-semibold text-white mt-0.5 leading-tight">{p.payor}</div>
        <div className="text-[11px] text-slate-400 mt-1 tabular-nums">
          {fmtNum(p.volume || 0)} claims · {share.toFixed(1)}% of total denials
        </div>
      </div>
      <div className="px-4 py-3 space-y-2.5">
        <TooltipRow label="At-Risk Exposure" value={fmtMoney(denied)} color="#FCA5A5" />
        <TooltipRow label="Estimated Recoverable" value={fmtMoney(recoverable)} color="#5EEAD4" />
        <TooltipRow label="Recovery Probability" value={`${probability.toFixed(1)}%`} color="#E2E8F0" mono />
      </div>
      <div className="px-4 pb-3.5 pt-2 border-t border-slate-700/40">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-semibold text-teal-300">
          <Sparkles className="h-2.5 w-2.5" />
          Intelligence Brief
        </div>
        <div className="text-[12px] text-slate-200 mt-1 leading-relaxed">{insight}</div>
      </div>
    </div>
  );
};

const TooltipRow = ({ label, value, color, mono }) => (
  <div className="flex items-center justify-between gap-6">
    <span className="text-[12px] text-slate-300">{label}</span>
    <span className={`text-[13px] font-semibold tabular-nums ${mono ? "" : ""}`} style={{ color }}>
      {value}
    </span>
  </div>
);


const PayorDenialIntelligencePanel = ({ payors = [], onSelect = null }) => {
  const [selectedPayor, setSelectedPayor] = useState("__all__");
  const filtered = useMemo(
    () => selectedPayor === "__all__" ? payors : (payors || []).filter((p) => p.payor === selectedPayor),
    [payors, selectedPayor]
  );
  const top8 = useMemo(() => filtered.slice(0, 8), [filtered]);

  const totals = useMemo(() => {
    let denied = 0, recoverable = 0, volume = 0;
    for (const p of filtered) {
      denied += p.dollars || 0;
      recoverable += p.recoverable_dollars || 0;
      volume += p.volume || 0;
    }
    const recoveryRate = denied > 0 ? (recoverable / denied) * 100 : 0;
    return { denied, recoverable, volume, recoveryRate };
  }, [filtered]);

  // Semantic financial palette
  const RISK = "#E5736F";          // muted coral – financial leakage, NOT alarming
  const RISK_DEEP = "#C2554F";
  const OPP = "#2DD4BF";           // turquoise/teal — recoverable opportunity
  const OPP_DEEP = "#14B8A6";      // teal-500 (brand)

  if (!top8.length) {
    return (
      <div className="surface-card p-6" data-testid="payor-analytics">
        <SectionHeader overline="Strategic Revenue Risk" title="Payor Denial Intelligence" />
        <div className="text-sm py-8 text-center text-slate-500">No payor data yet.</div>
      </div>
    );
  }

  return (
    <div className="surface-card p-6" data-testid="payor-analytics">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: "#2DD4BF" }}>
            Strategic Revenue Risk
          </div>
          <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight mt-1.5 text-slate-900">
            Payor Denial Intelligence
          </h2>
          <div className="text-xs text-slate-700 mt-1 font-medium">
            Prioritized recoverability analysis by payor · Top {top8.length}
          </div>
          <div className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1.5">
            <Sparkles className="h-2.5 w-2.5" style={{ color: "#2DD4BF" }} />
            Recoverability estimates modeled from historical denial resolution trends
          </div>
        </div>
        {payors.length > 1 && (
          <select
            value={selectedPayor}
            onChange={(e) => setSelectedPayor(e.target.value)}
            data-testid="payor-intelligence-filter"
            className="shrink-0 h-8 rounded-md border border-slate-300 bg-white px-2 text-[11px] font-semibold text-slate-700 max-w-[200px] truncate focus:outline-none focus:ring-2 focus:ring-teal-400/40"
            title="Filter to a specific payor"
          >
            <option value="__all__">All Payors ({payors.length})</option>
            {payors.map((p) => (
              <option key={p.payor} value={p.payor}>{p.payor}</option>
            ))}
          </select>
        )}
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider text-slate-600 font-semibold">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: RISK }} />
            At-Risk
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: OPP_DEEP, boxShadow: `0 0 0 2px ${OPP_DEEP}22` }} />
            Recoverable
          </span>
        </div>
      </div>

      {/* Top KPI tiles */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="rounded-xl p-3.5 border transition-all hover:shadow-md" style={{ background: "rgba(248,113,113,0.10)", borderColor: "rgba(248,113,113,0.40)" }} data-testid="payor-kpi-denied">
          <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#FCA5A5" }}>Total at Risk</div>
          <div className="font-display text-xl font-semibold mt-1 text-slate-900 tabular-nums">{fmtMoney(totals.denied)}</div>
          <div className="text-[11px] text-slate-600 mt-0.5">Across {fmtNum(totals.volume)} denied claims</div>
        </div>
        <div className="rounded-xl p-3.5 border transition-all hover:shadow-md" style={{ background: "rgba(45,212,191,0.08)", borderColor: "rgba(45,212,191,0.40)" }} data-testid="payor-kpi-recoverable">
          <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#2DD4BF" }}>Estimated Recoverable Revenue</div>
          <div className="font-display text-xl font-semibold mt-1 text-slate-900 tabular-nums">{fmtMoney(totals.recoverable)}</div>
          <div className="text-[11px] text-slate-600 mt-0.5">Predicted recoverable dollars from denial patterns</div>
        </div>
        <div className="rounded-xl p-3.5 border bg-slate-50 border-slate-200 transition-all hover:shadow-md" data-testid="payor-kpi-recovery-rate">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-700">Recovery Rate</div>
          <div className="font-display text-xl font-semibold mt-1 text-slate-900 tabular-nums">{fmtPct(totals.recoveryRate)}</div>
          <div className="text-[11px] text-slate-600 mt-0.5">Of denied dollars estimated recoverable</div>
        </div>
      </div>

      {/* Bar chart — clickable bars route to payor drill-down */}
      <div style={{ height: Math.max(280, top8.length * 56) }} data-testid="payor-analytics-chart" className="payor-intel-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={top8}
            layout="vertical"
            margin={{ top: 4, right: 72, left: 0, bottom: 0 }}
            barGap={6}
            barCategoryGap={20}
            onClick={(e) => {
              const payload = e && e.activePayload && e.activePayload[0] && e.activePayload[0].payload;
              if (payload && typeof onSelect === "function") onSelect(payload);
            }}
          >
            <defs>
              <linearGradient id="grad-risk" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={RISK} stopOpacity={0.85} />
                <stop offset="100%" stopColor={RISK_DEEP} stopOpacity={0.95} />
              </linearGradient>
              <linearGradient id="grad-opp" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={OPP} stopOpacity={0.95} />
                <stop offset="100%" stopColor={OPP_DEEP} stopOpacity={1} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#EEF2F7" strokeDasharray="3 3" horizontal={false} />
            <XAxis
              type="number"
              stroke="#64748B"
              fontSize={11}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => fmtMoney(v)}
            />
            <YAxis
              dataKey="payor"
              type="category"
              stroke="#0F172A"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              width={140}
              tick={{ fontWeight: 700 }}
            />
            <Tooltip
              content={<PayorIntelligenceTooltip totalDenied={totals.denied} />}
              cursor={{ fill: "rgba(20,184,166,0.06)" }}
              wrapperStyle={{ outline: "none" }}
              animationDuration={150}
            />
            <Bar
              dataKey="dollars"
              name="At-Risk $"
              fill="url(#grad-risk)"
              radius={[0, 6, 6, 0]}
              barSize={14}
              isAnimationActive
              animationDuration={800}
              animationEasing="ease-out"
              style={{ cursor: onSelect ? "pointer" : "default" }}
            >
              <LabelList
                dataKey="dollars"
                position="right"
                formatter={(v) => fmtMoney(v)}
                style={{ fill: RISK_DEEP, fontSize: 10, fontWeight: 700 }}
              />
            </Bar>
            <Bar
              dataKey="recoverable_dollars"
              name="Recoverable $"
              fill="url(#grad-opp)"
              radius={[0, 6, 6, 0]}
              barSize={22}
              isAnimationActive
              animationDuration={800}
              animationEasing="ease-out"
              style={{ cursor: onSelect ? "pointer" : "default" }}
            >
              <LabelList
                dataKey="recoverable_dollars"
                position="right"
                formatter={(v) => fmtMoney(v)}
                style={{ fill: "#0D9488", fontSize: 11, fontWeight: 700 }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {onSelect && (
        <div className="mt-3 text-[11px] text-slate-500 italic flex items-center gap-1.5">
          <Sparkles className="h-2.5 w-2.5" style={{ color: "#2DD4BF" }} />
          Click any payor to open operational intelligence — top denial drivers, ANSI codes, and AI-recommended actions.
        </div>
      )}

      {/* Compact detail table */}
      <div className="overflow-x-auto mt-5">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="py-2 font-semibold">Payor</th>
              <th className="py-2 font-semibold text-right">Claims</th>
              <th className="py-2 font-semibold text-right">Preventable %</th>
              <th className="py-2 font-semibold text-right">Recovery %</th>
            </tr>
          </thead>
          <tbody>
            {top8.slice(0, 5).map((p) => {
              const recoveryPct = p.dollars > 0 ? (p.recoverable_dollars / p.dollars) * 100 : 0;
              return (
                <tr key={p.payor} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors">
                  <td className="py-2.5 font-medium text-slate-800">{p.payor}</td>
                  <td className="py-2.5 text-right text-slate-600 font-mono">{fmtNum(p.volume)}</td>
                  <td className="py-2.5 text-right text-slate-700 font-mono">{fmtPct(p.preventable_pct)}</td>
                  <td className="py-2.5 text-right font-mono font-semibold" style={{ color: "#2DD4BF" }}>{fmtPct(recoveryPct)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};






// Inline KPI-slot card shown when the dataset has no submitted-claims denominator.
// Lets the customer type the number once to unlock Denial Rate + First Pass.
const UnlockDenialRateCard = ({ onSubmitted }) => {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault?.();
    const n = parseInt(val, 10);
    if (!Number.isFinite(n) || n <= 0) { toast.error("Enter a positive number."); return; }
    setBusy(true);
    try {
      await api.patch("/dataset/submitted-claims", { submitted_claims_count: n });
      toast.success("Denial Rate unlocked");
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    } finally { setBusy(false); }
  };
  return (
    <div
      data-testid="kpi-card-unlock-denial-rate"
      className="relative overflow-hidden rounded-2xl border-2 border-dashed border-teal-500/40 bg-teal-50/40 p-4 flex flex-col justify-between min-h-[140px]"
    >
      <div>
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-teal-700">
          <Sparkles className="h-3 w-3" />
          Unlock Denial Rate
        </div>
        <p className="text-[11px] text-slate-700 mt-2 leading-snug">
          Enter total claims submitted this period (count, not $).
        </p>
      </div>
      <form onSubmit={submit} className="mt-2 flex gap-1.5">
        <input
          type="number" min="1" inputMode="numeric"
          value={val} onChange={(e) => setVal(e.target.value)}
          placeholder="e.g., 5400"
          className="flex-1 min-w-0 h-8 rounded-md border border-teal-300 bg-white px-2 text-xs font-semibold"
          data-testid="unlock-submitted-input"
        />
        <button
          type="submit" disabled={busy}
          className="h-8 px-2.5 rounded-md bg-teal-600 hover:bg-teal-500 text-white text-[11px] font-bold disabled:opacity-60"
          data-testid="unlock-submitted-submit"
        >
          {busy ? "…" : "Save"}
        </button>
      </form>
    </div>
  );
};


// Editable Denial Rate KPI card. Renders the normal KPI tile with an inline ✏️
// pencil that flips the card into an editor so the user can correct the
// denominator (`submitted_claims_count`) without re-uploading the dataset.
const DenialRateKpiCard = ({ rate, grade, period, submittedCount, onUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(submittedCount ? String(submittedCount) : "");
  const [busy, setBusy] = useState(false);

  const openEdit = () => {
    setVal(submittedCount ? String(submittedCount) : "");
    setEditing(true);
  };
  const cancelEdit = () => {
    setVal(submittedCount ? String(submittedCount) : "");
    setEditing(false);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const n = parseInt(val, 10);
    if (!Number.isFinite(n) || n <= 0) { toast.error("Enter a positive number."); return; }
    if (n === submittedCount) { setEditing(false); return; }
    setBusy(true);
    try {
      await api.patch("/dataset/submitted-claims", { submitted_claims_count: n });
      toast.success("Denial Rate updated");
      setEditing(false);
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    } finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div
        data-testid="kpi-card-denial-rate-editor"
        className="relative overflow-hidden rounded-xl p-5 min-h-[140px] flex flex-col justify-between"
        style={{
          background: "linear-gradient(160deg, #0B1524 0%, #122236 55%, #14283F 100%)",
          border: "1px solid rgba(94,234,212,0.32)",
          boxShadow: "0 12px 28px -18px rgba(0,0,0,0.80), 0 0 0 1px rgba(94,234,212,0.10) inset",
        }}
      >
        <div className="flex items-start justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-teal-200">
            Edit Claims Submitted (Count)
          </div>
          <button
            type="button"
            onClick={cancelEdit}
            className="text-slate-400 hover:text-white transition-colors"
            aria-label="Cancel edit"
            data-testid="denial-rate-edit-cancel"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={submit} className="mt-3 space-y-2">
          <input
            type="number" inputMode="numeric"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="Total claims submitted (count, not $)"
            autoFocus
            className="w-full h-9 rounded-md border border-teal-400/40 bg-slate-900/60 px-2.5 text-sm font-semibold text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-400/40"
            data-testid="denial-rate-edit-input"
          />
          <div className="flex gap-1.5">
            <button
              type="submit" disabled={busy}
              className="flex-1 h-8 rounded-md bg-teal-500 hover:bg-teal-400 text-white text-[11px] font-bold disabled:opacity-60 transition-colors"
              data-testid="denial-rate-edit-submit"
            >
              {busy ? "Saving…" : "Save & Recalculate"}
            </button>
          </div>
          <p className="text-[10px] text-slate-400 leading-snug">
            Must exceed denied claims in the active dataset.
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="relative">
      <KpiCard
        tone="light"
        icon={TrendingDown}
        label="Denial Rate"
        value={fmtPct(rate)}
        sub="Of Submitted Claims"
        helperText="% of claims denied by payors on initial submission"
        testId="kpi-card-denial-rate"
        grade={grade}
        period={period}
      />
      <button
        type="button"
        onClick={openEdit}
        className="absolute top-2 right-2 z-10 inline-flex items-center justify-center h-6 w-6 rounded-md bg-slate-800/60 hover:bg-teal-500/30 border border-teal-300/20 hover:border-teal-300/60 text-teal-200 hover:text-white transition-colors"
        title={`Edit submitted claims (${submittedCount?.toLocaleString?.() || "—"})`}
        aria-label="Edit submitted claims count"
        data-testid="denial-rate-edit-button"
      >
        <Pencil className="h-3 w-3" />
      </button>
    </div>
  );
};


export default function DashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [drillDown, setDrillDown] = useState(null); // "preventable" | "recoverable" | null

  const fetchDashboard = async ({ refresh = false } = {}) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const { data } = await api.get("/dashboard", { params: refresh ? { refresh: true } : {} });
      setData(data);
      if (refresh) toast.success("Dashboard refreshed");
    } catch (err) {
      toast.error("Failed to load dashboard");
    } finally {
      if (refresh) setRefreshing(false); else setLoading(false);
    }
  };

  const onDataRefresh = () => fetchDashboard({ refresh: true });

  const fetchInsights = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.get("/insights");
      setAi(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load AI insights");
    } finally { setAiLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchDashboard(); }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (data?.has_data && !ai) fetchInsights(); }, [data, ai]);

  const handleExport = async (fmt) => {
    setExporting(fmt);
    try {
      const resp = await api.get(`/export/${fmt}`, { responseType: "blob" });
      const isPdf = fmt === "pdf" || fmt === "executive-summary";
      const mime =
        fmt === "csv" ? "text/csv" :
        fmt === "excel" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" :
        "application/pdf";
      const filename =
        fmt === "csv" ? "rciq_denials.csv" :
        fmt === "excel" ? "rciq_executive_report.xlsx" :
        fmt === "executive-summary" ? "rciq_executive_summary.pdf" :
        "rciq_executive_report.pdf";
      const blob = new Blob([resp.data], { type: mime });
      const url = URL.createObjectURL(blob);

      if (isPdf) {
        // PDFs: open in a new browser tab — bypasses iframe sandbox download
        // blocking that affects some hosted preview environments and certain
        // production browsers. User can save from the new tab with Cmd/Ctrl+S.
        const win = window.open(url, "_blank");
        if (!win) {
          // Popup blocked — fall back to anchor download
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          a.rel = "noopener";
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
      } else {
        // CSV / Excel: anchor download is fine (browsers don't try to render these)
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }

      // Delay URL revocation so the browser has time to handle the download/open
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(
        fmt === "executive-summary" ? "Executive Summary opened — Cmd/Ctrl+S to save" :
        isPdf ? "Full PDF opened — Cmd/Ctrl+S to save" :
        `Exported ${fmt.toUpperCase()}`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally { setExporting(null); }
  };

  const top5 = data?.top_denials || [];
  const totalTop5 = useMemo(() => top5.reduce((s, t) => s + t.dollars, 0), [top5]);

  if (loading) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center py-32 text-slate-500">
          <Loader2 className="h-10 w-10 animate-spin text-[#0F4C81]" />
          <div className="mt-4 text-sm">Loading executive dashboard...</div>
        </div>
      </AppShell>
    );
  }

  if (!data?.has_data) {
    return (
      <AppShell>
        <div className="max-w-5xl mx-auto py-10">
          <PlanUsageWidget />
          <div className="max-w-2xl mx-auto text-center py-12">
            <div className="h-16 w-16 mx-auto rounded-2xl bg-soft-blue flex items-center justify-center mb-6"><Activity className="h-8 w-8 text-[#0F4C81]" /></div>
            <h2 className="font-display text-3xl font-semibold text-slate-900">No dataset loaded yet</h2>
            <p className="text-slate-500 mt-3">Upload a CSV/Excel file or try the demo dataset to launch the Denial Intelligence & Recovery Workflow Platform.</p>
            <Button data-testid="goto-upload-button" onClick={() => navigate("/upload")} className="mt-6 bg-navy hover:bg-[#0B3A66] text-white">Upload or Try Demo</Button>
          </div>
        </div>
      </AppShell>
    );
  }

  const k = data.kpis;

  return (
    <AppShell>
      <div className="space-y-7 fade-up">
        {/* Header bar */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <span className="label-overline">Denial Intelligence & Recovery Workflow</span>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-1.5 tracking-tight">
              {data.dataset?.name || "Denial Snapshot"}
            </h1>
            <div className="text-sm text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
              <span>{fmtNum(k.total_claims)} de-identified claims · {data.monthly_trends?.length || 0} {(data.monthly_trends?.length || 0) === 1 ? "month" : "months"}</span>
              {data.dataset?.vertical && data.dataset.vertical !== "mixed" && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-soft-blue text-[#0F4C81] text-[11px] font-semibold uppercase tracking-wider" data-testid="vertical-badge">
                  {data.dataset.vertical}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              data-testid="refresh-dashboard-button"
              onClick={() => fetchDashboard({ refresh: true })}
              disabled={refreshing}
              className="border-slate-300 rounded-lg"
              title="Recompute the dashboard from the latest dataset (bypasses cache)"
            >
              {refreshing
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
              Refresh
            </Button>
            <Button variant="outline" size="sm" data-testid="export-csv-button" onClick={() => handleExport("csv")} disabled={exporting === "csv"} className="border-slate-300 rounded-lg">
              {exporting === "csv" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileType className="h-3.5 w-3.5 mr-1.5" />}CSV
            </Button>
            <Button variant="outline" size="sm" data-testid="export-excel-button" onClick={() => handleExport("excel")} disabled={exporting === "excel"} className="border-slate-300 rounded-lg">
              {exporting === "excel" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />}Excel
            </Button>
            <Button variant="outline" size="sm" data-testid="export-pdf-button" onClick={() => handleExport("pdf")} disabled={exporting === "pdf"} className="border-slate-300 rounded-lg">
              {exporting === "pdf" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileText className="h-3.5 w-3.5 mr-1.5" />}Full PDF
            </Button>
            <Button
              size="sm"
              data-testid="export-executive-summary-button"
              onClick={() => handleExport("executive-summary")}
              disabled={exporting === "executive-summary"}
              className="ai-cta rounded-lg px-4"
              title="One-page board-ready CFO snapshot"
            >
              {exporting === "executive-summary"
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
              Executive Summary
            </Button>
          </div>
        </div>

        {/* PHI banner (compact) */}
        <div data-testid="dashboard-phi-notice" className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-2.5 flex items-center gap-2 text-xs text-amber-900">
          <ShieldCheck className="h-3.5 w-3.5 text-amber-700" />
          <span><strong>De-identified analytics only.</strong> No PHI is displayed, stored, or processed.</span>
        </div>

        {/* Human + AI differentiator ribbon */}
        <HumanInTheLoopCard variant="ribbon" />

        {/* Plan & usage — only renders for non-Enterprise customers */}
        <PlanUsageWidget />

        {/* KPI strip - 7 cards. Benchmark pills only on metrics where industry comparison matters. */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-3.5">
          <KpiCard
            hero
            tone="navy"
            icon={DollarSign}
            label="Total Denied Dollars"
            value={fmtMoney(k.total_denied_dollars)}
            sub={`${fmtNum(k.total_claims)} Claims`}
            testId="kpi-card-total-denied"
            period={data?.comparison?.metrics?.total_denied_dollars}
          />
          {k.has_submitted_count ? (
            <DenialRateKpiCard
              rate={k.denial_rate}
              grade={data?.benchmarks?.grades?.denial_rate}
              period={data?.comparison?.metrics?.denial_rate}
              submittedCount={k.submitted_claims_count}
              onUpdated={onDataRefresh}
            />
          ) : (
            <UnlockDenialRateCard onSubmitted={onDataRefresh} />
          )}
          <KpiCard
            tone="light"
            icon={ShieldCheck}
            label="Preventable Denials"
            value={fmtPct(k.preventable_pct)}
            sub="Of All Denials"
            helperText="Eligibility, authorization, coding, or registration errors"
            testId="kpi-card-preventable"
            grade={data?.benchmarks?.grades?.preventable_pct}
            period={data?.comparison?.metrics?.preventable_pct}
            onClick={() => setDrillDown("preventable")}
          />
          <KpiCard
            tone="navy"
            icon={RefreshCw}
            label="Recoverable Revenue"
            value={fmtMoney(k.recoverable_dollars)}
            sub="Estimated Recoverable Through Appeals"
            helperText="High-probability recovery based on CARC patterns"
            testId="kpi-card-recoverable"
            period={data?.comparison?.metrics?.recoverable_dollars}
            onClick={() => setDrillDown("recoverable")}
          />
          <KpiCard
            tone="light"
            icon={AlertOctagon}
            label="Top Payor Risk"
            value={<span className="text-base lg:text-lg leading-tight block truncate">{k.top_payor}</span>}
            sub={(() => {
              const topPayor = (data?.payors || []).find((p) => p.payor === k.top_payor);
              if (!topPayor) return "—";
              const share = k.total_denied_dollars ? (topPayor.dollars / k.total_denied_dollars) * 100 : 0;
              return `${fmtMoney(topPayor.dollars)} · ${share.toFixed(0)}% of denials`;
            })()}
            helperText="Highest concentration of denied dollars"
            testId="kpi-card-top-payor"
          />
          {k.has_submitted_count ? (
            <KpiCard
              tone="light"
              icon={Activity}
              label="First Pass Acceptance"
              value={fmtPct(k.first_pass_estimate)}
              sub="Accepted on First Submission"
              testId="kpi-card-first-pass"
              grade={data?.benchmarks?.grades?.first_pass_estimate}
              period={data?.comparison?.metrics?.first_pass_estimate}
              onClick={() => setDrillDown("first_pass")}
            />
          ) : (
            <KpiCard
              tone="light"
              icon={Activity}
              label="Denied Claims"
              value={fmtNum(k.total_claims)}
              sub="Claims in dataset"
              helperText="Add total submitted claims (above) to unlock First Pass Acceptance %"
              testId="kpi-card-denied-count"
            />
          )}
          <KpiCard
            tone="light"
            icon={Clock}
            label="Average Days in AR"
            value={`${k.avg_aging_days} d`}
            sub="Days Outstanding"
            testId="kpi-card-avg-aging"
            grade={data?.benchmarks?.grades?.avg_aging_days}
            period={data?.comparison?.metrics?.avg_aging_days}
            onClick={() => setDrillDown("avg_aging")}
          />
        </div>

        {/* Executive Recovery Outlook — CFO-grade recoverable revenue calculator */}
        <RecoveryOutlookPanel outlook={data.recovery_outlook} />

        {/* Industry benchmark legend */}
        {data?.benchmarks?.vertical && (
          <div data-testid="benchmark-legend" className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs text-slate-600 flex flex-wrap items-center gap-x-5 gap-y-1.5">
            <span className="font-semibold uppercase tracking-wider text-[10px] text-slate-500">Benchmarked vs</span>
            <span className="font-medium text-slate-900 capitalize">{data.benchmarks.vertical} industry medians</span>
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: "#0F766E" }} /> Ahead of median</span>
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: "#94A3B8" }} /> On track</span>
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: "#B91C1C" }} /> Behind median</span>
            {data?.comparison?.available && (
              <span className="ml-auto text-slate-500">
                Period: <span className="font-medium text-slate-700">{data.comparison.current_months?.join(", ")}</span> vs <span className="font-medium text-slate-700">{data.comparison.prior_months?.join(", ")}</span>
              </span>
            )}
          </div>
        )}

        {/* Trend chart + AI Insights */}
        <div className="grid lg:grid-cols-3 gap-5">
          <div
            className="surface-card p-6 lg:col-span-2 relative overflow-hidden"
            data-testid="monthly-trend-chart"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: "#2DD4BF" }}>
                  Denial Trend
                </div>
                <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight mt-1.5 text-slate-900">
                  Monthly Denial Volume
                </h2>
              </div>
              <DeltaPill delta={data?.comparison?.metrics?.total_denied_dollars} />
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.monthly_trends} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gDollars" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#14B8A6" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#14B8A6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => fmtMoney(v)} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area type="monotone" dataKey="dollars" name="Denied $" stroke="#14B8A6" strokeWidth={2.5} fill="url(#gDollars)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Opportunity card */}
          <TopOpportunityCard data={data} />
        </div>

        {/* Top 5 Denial Drivers (operational, not raw codes) + Payor Analytics */}
        <div className="grid lg:grid-cols-2 gap-5">
          <ActionabilityCard actionability={data.actionability} />
          <TopDenialDriversPanel
            drivers={data.denial_drivers || []}
            months={data.monthly_trends?.length || 0}
            onSelect={(driver) => setDrillDown({ kind: "driver", driver })}
          />
        </div>

        {/* Denials by Payor — dedicated full-width row */}
        <div className="grid lg:grid-cols-1 gap-5">
          <DenialsByPayorPanel
            payors={data.payors || []}
            totalClaims={k.total_claims}
            onSelect={(payor) => setDrillDown({ kind: "payor", payor })}
          />
        </div>

        {/* Payor Denial Intelligence — strategic revenue risk vs recoverable opportunity */}
        <PayorDenialIntelligencePanel
          payors={data.payors || []}
          onSelect={(payor) => setDrillDown({ kind: "payor", payor })}
        />

        {/* AI Intelligence Trends — Top 5 ANSI Denial Codes */}
        <DenialIntelligenceTrendsPanel trends={data.top_ansi_codes || []} />

        {/* Top Payor Risk — premium AI-powered predictive table with inline drill-down */}
        <TopPayorRiskPanel
          payorRisk={data.payor_risk || []}
          payors={data.payors || []}
          denialDrivers={data.denial_drivers || []}
          onSelect={(p) => setDrillDown({ kind: "payor", payor: { payor: p.payor, dollars: p.dollars, recoverable_dollars: p.recoverable_dollars } })}
        />

        {/* Denial Workflow & Appeal Intelligence — bottom of dashboard */}
        <AppealRecommendations topDenials={data.top_denials || []} topPayor={data.payors?.[0]?.payor || k.top_payor} />
      </div>

      {/* KPI drill-down modal */}
      {drillDown && (
        <KpiDrillDownModal
          drill={typeof drillDown === "string" ? { kind: drillDown } : drillDown}
          data={data}
          onClose={() => setDrillDown(null)}
          onNavigate={(path) => { setDrillDown(null); navigate(path); }}
        />
      )}
    </AppShell>
  );
}

